<template>
  <modal
    v-if="show"
    height="865px"
    width="600px"
    bodyPadding="0"
    :title="t('Campaign Links')"
    @close="close"
  >
    <template v-if="step === 1">
      <div class="dialog-wrapper p-24">
        <div class="details campaign-links">
          <div class="campaign-details__wrap">
            <div>
              <ui-copy-input
                :label="t('Campaign name')"
                v-model="campaign.name"
              ></ui-copy-input>
            </div>

            <div>
              <ui-copy-input class="copy-qr__popup" v-model="form.campaignRedirectUrl">
                <template #label>
                  <label>
                    <b>{{ t('Campaign URL') }}</b>
                    <v-menu bottom open-on-hover offset-y offset-x :transition="false">
                      <template #activator="{ props: activatorProps }">
                        <a v-bind="activatorProps" class="mb-2">
                          <ui-icon size="14" :color="$colors.blue" class="ml-6" icon="fas fa-qrcode"></ui-icon>
                          <b>{{ ' ' + t('QR') }}</b>
                        </a>
                      </template>
                      <v-list>
                        <img :src="form.campaignRedirectUrlQR" alt="qr code">
                      </v-list>
                    </v-menu>
                    <ui-chip
                      :contentColor="$colors.white"
                      chipStyle="round"
                      class="ssl-pending ml-4"
                      v-if="trackingDomain && !trackingDomain.httpsAvailable && !isNextDnsRecordValidationAfterYear(trackingDomain)"
                    >
                      SSL Pending
                    </ui-chip>
                    <ui-chip
                      :contentColor="$colors.white"
                      chipStyle="round"
                      :backgroundColor="$colors.green"
                      class="ml-4 d-inline-block"
                      v-if="trackingDomain && isNextDnsRecordValidationAfterYear(trackingDomain)"
                    >
                      {{ t('Client-managed domain') }}
                    </ui-chip>
                  </label>
                </template>
              </ui-copy-input>
            </div>

            <div>
              <ui-copy-input class="copy-qr__popup" v-model="form.campaignRedirectTestUrl">
                <template #label>
                  <label>
                    <b>{{ t('Campaign test URL') }}</b>
                    <v-menu bottom open-on-hover offset-y offset-x :transition="false">
                      <template #activator="{ props: activatorProps }">
                        <a v-bind="activatorProps" class="mb-2">
                          <ui-icon size="14" :color="$colors.blue" class="ml-6" icon="fas fa-qrcode"></ui-icon>
                          <b>{{ ' ' + t('QR') }}</b>
                        </a>
                      </template>
                      <v-list>
                        <img :src="form.campaignRedirectTestUrlQR">
                      </v-list>
                    </v-menu>
                  </label>
                </template>
              </ui-copy-input>
            </div>

            <div>
              <ui-copy-input
                :label="t('Campaign redirect JS script')"
                v-model="form.campaignJsRedirect"
              ></ui-copy-input>
            </div>

            <div v-if="campaign.destinationType === 'flow' || campaign.destinationType === 'flowInline'">
              <ui-copy-input
                :label="t('Landing pixel')"
                v-model="form.campaignLandingPixel"
              >
                <template #label>
                  <label>
                    <b>{{ t('Landing pixel') }}</b>
                    <a class="mb-2" @click.stop="openLpPixelDialog">
                      <ui-icon size="14" :color="$colors.blue" class="ml-6" icon="fas fa-cogs"></ui-icon>
                      <b>{{ ' ' + t('LP Pixel Generator') }}</b>
                    </a>
                  </label>
                </template>
              </ui-copy-input>
            </div>

            <template v-if="campaign.trafficSource && campaign.trafficSource.trackImpressions">
              <div>
                <ui-copy-input
                  :label="t('Impression URL')"
                  v-model="form.campaignImpressionsUrl"
                ></ui-copy-input>
              </div>

              <div>
                <ui-copy-input
                  :label="t('Impression pixel')"
                  v-model="form.campaignImpressionsPixel"
                ></ui-copy-input>
              </div>
            </template>

            <div>
              <ui-copy-input
                :label="t('Click URL')"
                v-model="form.clickUrl"
              ></ui-copy-input>
            </div>

            <div>
              <ui-copy-input
                :label="t('Multi-offer click URL')"
                v-model="form.multiClickUrl"
              ></ui-copy-input>
            </div>

            <button
              :disabled="campaign.destinationType === 'url'"
              class="mb-12 track-conversions-no-click-id-button"
              @click.stop="openTrackConversionsWithNoClickIdsDialog({ linkType: 'postbackUrlTxt' })"
              v-if="store.user.trackConversionsNoClickId"
            >
              <ui-icon size="14" :color="$colors.blue" icon="fas fa-cogs"></ui-icon>
              <b class="generate-links">{{ ' ' + t('Track Conversions With No Click ID') }}</b>
            </button>

            <div>
              <ui-copy-input
                v-model="form.postbackUrl"
              >
                <template #label>
                  <b>{{ t('Postback URL') }}</b>
                  <router-link
                    :to="{ name: 'app.profile.custom-conversions'}"
                    custom
                    v-slot="{ href }"
                  >
                    <a class="generate-links open-modal" target="_blank" :href="href">
                      <ui-icon size="14" :color="$colors.blue" class="ml-6" icon="fas fa-cogs"></ui-icon>
                      <b>{{ ' ' + t('Custom Conversions Tracking') }}</b>
                    </a>
                  </router-link>
                </template>
              </ui-copy-input>
            </div>

            <div>
              <ui-copy-input
                v-model="form.trackingPixel"
              >
                <template #label>
                  <b>{{ t('Postback pixel') }}</b>
                  <router-link
                    :to="{ name: 'app.profile.custom-conversions'}"
                    custom
                    v-slot="{ href }"
                  >
                    <a class="generate-links open-modal" target="_blank" :href="href">
                      <ui-icon size="14" :color="$colors.blue" class="ml-6" icon="fas fa-cogs"></ui-icon>
                      <b>{{ ' ' + t('Custom Conversions Tracking') }}</b>
                    </a>
                  </router-link>
                </template>
              </ui-copy-input>
            </div>

            <div>
              <ui-copy-input v-model="form.trackingScript">
                <template #label>
                  <b>{{ t('Postback script') }}</b>
                  <router-link
                    :to="{ name: 'app.profile.custom-conversions'}"
                    custom
                    v-slot="{ href }"
                  >
                    <a class="generate-links open-modal" target="_blank" :href="href">
                      <ui-icon size="14" :color="$colors.blue" class="ml-6" icon="fas fa-cogs"></ui-icon>
                      <b>{{ ' ' + t('Custom Conversions Tracking') }}</b>
                    </a>
                  </router-link>
                </template>
              </ui-copy-input>
            </div>

            <div>
              <ui-copy-input v-model="form.campaignImpressionsUrlTxt">
                <template #label>
                  <b>{{ t('Postback URL (for TXT response)') }}</b>

                  <ui-tooltip-icon
                    class="ml-4 ml-first-0"
                    preset="help"
                    :text="t('This postback URL is required in cases when your affiliate network is requiring the response on successful conversion action in TXT format.')"
                  ></ui-tooltip-icon>

                  <router-link
                    :to="{ name: 'app.profile.custom-conversions'}"
                    custom
                    v-slot="{ href }"
                  >
                    <a class="generate-links open-modal" target="_blank" :href="href">
                      <ui-icon size="14" :color="$colors.blue" class="ml-6" icon="fas fa-cogs"></ui-icon>
                      <b class="generate-links">{{ ' ' + t('Custom Conversions Tracking') }}</b>
                    </a>
                  </router-link>
                </template>
              </ui-copy-input>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <section class="footer xs12" v-if="step === 1">
        <ui-button
          :text="t('Close')"
          icon="fas fa-times"
          @click="close"
        ></ui-button>
        <ui-button
          :text="t('Edit')"
          icon="far fa-edit"
          button-style="accent"
          @click="handleEdit"
        ></ui-button>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { useColors } from '@/composables';
import {
  useCampaignLinksForm,
  type CampaignLinksDialogData,
} from './useCampaignLinksForm';

const $colors = useColors();

const props = withDefaults(
  defineProps<{
    data?: CampaignLinksDialogData;
  }>(),
  {
    data: () => ({}),
  },
);

const emit = defineEmits<{
  close: [];
}>();

const {
  t,
  store,
  step,
  show,
  trackingDomain,
  campaign,
  form,
  isNextDnsRecordValidationAfterYear,
  openLpPixelDialog,
  openTrackConversionsWithNoClickIdsDialog,
  handleEdit,
  close,
} = useCampaignLinksForm(() => props.data, () => emit('close'));
</script>

<style lang="scss" scoped>
@import '@/styles/dialogs/report-modals';

.details.campaign-links {
  flex: 0 1 100%;
  max-width: 100%;
}

.open-modal{
  position:relative;
  z-index:1
}

.footer {
  display: flex;
  justify-content: flex-end;
}
.generate-links{
  text-decoration: none;
  color: var(--color-blue);
}

.text-why-campaign-links{
  font-weight: normal;
  text-transform: none;
}

.ssl-pending{
  background: var(--color-red);
  display: inline-block;
  margin-right: 0 !important
}
</style>

<style lang="scss">
.campaign-links .copy-input.v-input.copy-input {
  padding-bottom: 16px !important;
}

.copy-qr__popup a.mb-2 {
  display: inline-block;
}

.track-conversions-no-click-id-button:disabled{
  opacity: 0.8;
}

.links-form-dialog {
  height: 100%;
  max-height: 865px !important;

  .v-card {
    height: 100%;
    max-height: 865px;
  }
}
</style>
